import { yupResolver } from '@hookform/resolvers/yup'
import { ReactNode, useEffect, useMemo } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { FeatureFlag, WhenFlag } from '@/feature/feature-flags'
import { TableCellTags, TagsModalContainer, TagsPopout } from '@/feature/tags'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from '@/lib/core/components/Button'
import { H6 } from '@/lib/core/components/Text'
import { TextField } from '@/lib/core/components/TextField'
import { Nullable } from '@/lib/core/interfaces/common.types'
import { MakeFormState } from '@/lib/core/interfaces/forms.types'
import { formsMessages } from '@/lib/core/messages'

import { usePatchPTU, usePatchVehicle } from '@/lib/features/Assets/api'
import { useTrackingSourceOptions } from '@/lib/features/Assets/components/AddAssetModal/hooks/useTrackingSourceOptions'
import {
  AssetViewFormFooter,
  AssetViewFormTable,
  DataCell,
  FormBox,
  LabelCell,
} from '@/lib/features/Assets/components/AssetView/AssetView.styles'
import assetViewMessages from '@/lib/features/Assets/components/AssetView/assetView.messages'
import {
  Asset,
  AssetContentType,
  PatchMyPtuPayload,
  PatchMyPtuResponseError,
  PatchMyVehiclePayload,
} from '@/lib/features/Assets/interfaces'

import { setFormErrors } from '@/lib/utils/forms'

type FieldRender = {
  component: Nullable<ReactNode>
  label: string
  value: number | string | null
  renderCondition?: boolean
}

export type AssetViewFormValues = PatchMyVehiclePayload
export type AssetViewFormProps = {
  isEditing: boolean
  isRealOwner: boolean
  isAssetShared: boolean
  onCancel: () => void
  onUpdate?: () => void
  defaultValues: Asset
}

type VehicleFormState = MakeFormState<PatchMyVehiclePayload>
type PtuFormState = MakeFormState<PatchMyPtuPayload>

export const AssetViewForm = ({
  defaultValues,
  isRealOwner,
  isEditing,
  onCancel,
  onUpdate = () => null,
  isAssetShared,
}: AssetViewFormProps) => {
  const { formatMessage } = useIntl()
  const trackingOptionsValues = useTrackingSourceOptions()
  const { trigger: patchAssetPTU } = usePatchPTU()
  const { trigger: patchAssetVehicle } = usePatchVehicle()

  const schema = useMemo(
    () =>
      ({
        [AssetContentType.VEHICLE]: yup.object().shape({
          description: yup
            .string()
            .nullable()
            .required(formatMessage(formsMessages.requiredValidation)),
          make: yup.string().nullable().optional(),
          model: yup.string().nullable().optional(),
          plateNumber: yup
            .string()
            .nullable()
            .required(formatMessage(formsMessages.requiredValidation)),
          trackingIdentifier: yup
            .string()
            .nullable()
            .required(formatMessage(formsMessages.requiredValidation)),
          trackingSourceId: yup
            .string()
            .nullable()
            .required(formatMessage(formsMessages.requiredValidation))
            .test(
              'is_option_enabled',
              formatMessage(formsMessages.disabledOption),
              (value) =>
                !!value &&
                !!trackingOptionsValues.find((option) => option.value.toString() === value),
            ),
          vin: yup.string().nullable().optional(),
        }),
        [AssetContentType.PTU]: yup.object().shape({
          description: yup
            .string()
            .nullable()
            .required(formatMessage(formsMessages.requiredValidation)),
          trackingIdentifier: yup
            .string()
            .nullable()
            .required(formatMessage(formsMessages.requiredValidation)),
          trackingSourceId: yup
            .string()
            .nullable()
            .required(formatMessage(formsMessages.requiredValidation))
            .test(
              'is_option_enabled',
              formatMessage(formsMessages.disabledOption),
              (value) =>
                !!value &&
                !!trackingOptionsValues.find((option) => option.value.toString() === value),
            ),
        }),
      })[defaultValues.contentType],
    [defaultValues.contentType, trackingOptionsValues, formatMessage],
  )

  const formDefaultValues = useMemo(
    () =>
      ({
        [AssetContentType.PTU]: {
          description: defaultValues.description,
          objectId: defaultValues.objectId,
          trackingIdentifier: defaultValues.trackingIdentifier,
          trackingSourceId: defaultValues.trackingSource.id,
        } as PtuFormState,
        [AssetContentType.VEHICLE]: {
          description: defaultValues.description,
          make: defaultValues.make,
          model: defaultValues.model,
          objectId: defaultValues.objectId,
          plateNumber: defaultValues.plateNumber,
          trackingIdentifier: defaultValues.trackingIdentifier,
          trackingSourceId: defaultValues.trackingSource.id,
          vin: defaultValues.vin,
        } as VehicleFormState,
      })[defaultValues.contentType],
    [defaultValues],
  )

  const formMethods = useForm<VehicleFormState>({
    defaultValues: formDefaultValues,
    resolver: yupResolver(schema) as unknown as Resolver<VehicleFormState>,
  })
  const { formState, handleSubmit, reset, setError, watch, trigger } = formMethods
  const { isSubmitting, isSubmitted, isValid } = formState
  const currentValues = watch()

  const trackingOptions = useMemo<
    {
      isDisabled?: boolean
      label: string
      value: number
    }[]
  >(() => {
    const hasCurrentOptionInTrackingSourceList = trackingOptionsValues.some(
      ({ value }) => value === currentValues.trackingSourceId,
    )

    if (!hasCurrentOptionInTrackingSourceList && defaultValues.trackingSource.id) {
      return [
        {
          isDisabled: true,
          label: defaultValues.trackingSource.name,
          value: defaultValues.trackingSource.id,
        },
        ...trackingOptionsValues,
      ]
    }

    return trackingOptionsValues
  }, [trackingOptionsValues, currentValues.trackingSourceId, defaultValues])

  const currentTrackingOption = useMemo(
    () => trackingOptions.find(({ value }) => value === currentValues.trackingSourceId),
    [trackingOptions, currentValues.trackingSourceId],
  )

  const renderFields = useMemo(() => {
    const fields: {
      [fieldName: string]: FieldRender
    } = {
      assetId: {
        component: defaultValues.bridgeId,
        label: formatMessage(assetViewMessages.assetId),
        value: defaultValues.bridgeId,
      },
      assetOwner: {
        component: defaultValues.owner.name,
        label: formatMessage(assetViewMessages.assetOwner),
        renderCondition: isAssetShared,
        value: defaultValues.owner.name,
      },
      description: {
        component: (
          <TextField
            id="edit-asset-form-description"
            displayInline
            name="description"
            type="text"
            disabled={isSubmitting}
            required
          />
        ),
        label: formatMessage(assetViewMessages.description),
        renderCondition: isEditing,
        value: null,
      },
      licensePlate: {
        component: (
          <TextField
            id="edit-asset-form-plateNumber"
            type="text"
            name="plateNumber"
            displayInline
            required
            disabled={isSubmitting}
          />
        ),
        label: formatMessage(assetViewMessages.vehicleLicensePlate),
        value: defaultValues.plateNumber,
      },
      trackingIdentifier: {
        component: defaultValues.trackingIdentifier,
        label: formatMessage(assetViewMessages.trackingIdentifier),
        renderCondition: isRealOwner,
        value: defaultValues.trackingIdentifier,
      },
      trackingSource: {
        component: defaultValues.trackingSource.name,
        label: formatMessage(assetViewMessages.trackingSource),
        value: defaultValues.trackingSource.name,
      },
      vehicleMake: {
        component: (
          <TextField
            id="edit-asset-form-make"
            displayInline
            type="text"
            name="make"
            disabled={isSubmitting}
          />
        ),
        label: formatMessage(assetViewMessages.vehicleMake),
        value: defaultValues.make,
      },
      vehicleModel: {
        component: (
          <TextField
            id="edit-asset-form-model"
            type="text"
            name="model"
            displayInline
            disabled={isSubmitting}
          />
        ),
        label: formatMessage(assetViewMessages.vehicleModel),
        value: defaultValues.model,
      },
      vin: {
        component: (
          <TextField
            id="edit-asset-form-vin"
            displayInline
            type="text"
            name="vin"
            disabled={isSubmitting}
          />
        ),
        label: formatMessage(assetViewMessages.vinNumber),
        renderCondition: isRealOwner,
        value: defaultValues.vin,
      },
    }

    const fieldsByType: { [key in AssetContentType]: Array<FieldRender> } = {
      [AssetContentType.VEHICLE]: [
        fields.assetId,
        fields.vin,
        fields.assetOwner,
        fields.vehicleMake,
        fields.vehicleModel,
        fields.licensePlate,
        fields.trackingSource,
        fields.trackingIdentifier,
        fields.description,
      ],
      [AssetContentType.PTU]: [
        fields.assetId,
        fields.assetOwner,
        fields.trackingSource,
        fields.trackingIdentifier,
        fields.description,
      ],
    }

    return fieldsByType[defaultValues?.contentType]
      .filter(({ renderCondition }) => typeof renderCondition === 'undefined' || renderCondition)
      .map(({ label, value, component }) => (
        <tr key={label}>
          <LabelCell>{`${label}:`}</LabelCell>
          <DataCell>{isEditing ? component : value || '-'}</DataCell>
        </tr>
      ))
  }, [
    currentTrackingOption,
    defaultValues,
    formatMessage,
    isAssetShared,
    isEditing,
    isRealOwner,
    isSubmitting,
    trackingOptions,
  ])

  const onFormCancel = () => {
    onCancel()
    reset()
  }

  const onSubmit = async (values: VehicleFormState) => {
    try {
      if (defaultValues.contentType === AssetContentType.PTU) {
        await patchAssetPTU({
          data: values as PatchMyPtuPayload,
          params: { objectId: values.objectId! },
        })
      } else if (defaultValues.contentType === AssetContentType.VEHICLE) {
        await patchAssetVehicle({
          data: values as PatchMyVehiclePayload,
          params: { objectId: values.objectId! },
        })
      }
      onUpdate()
    } catch (err) {
      const errors = err as PatchMyPtuResponseError

      if (errors) {
        setFormErrors(setError, errors)
      }
    }
  }

  useEffect(() => {
    if (currentTrackingOption?.isDisabled) {
      setError('trackingSourceId', {
        message: formatMessage(formsMessages.disabledOption),
        type: 'is_option_enabled',
      })
    } else {
      trigger('trackingSourceId')
    }
  }, [currentTrackingOption, formatMessage, setError, trigger])

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        {isEditing && isSubmitted && !isValid && (
          <FormBox variant="error">
            <FormattedMessage {...formsMessages.formHasErrors} />
          </FormBox>
        )}
        <AssetViewFormTable>
          <tbody>{renderFields}</tbody>
        </AssetViewFormTable>

        <FeatureFlag flag="assetTags">
          <WhenFlag>
            {defaultValues.tags && (
              <>
                <H6>{formatMessage(assetViewMessages.tags)}</H6>
                <TableCellTags assetData={defaultValues} maxTagsRenderCount={12} />
                <TagsPopout />
                <TagsModalContainer />
              </>
            )}
          </WhenFlag>
        </FeatureFlag>
        {isEditing && (
          <AssetViewFormFooter>
            <Button
              type="button"
              variant="ghost"
              $fitToContent
              disabled={isSubmitting}
              onClick={onFormCancel}
            >
              <FormattedMessage {...assetViewMessages.cancel} />
            </Button>
            <Button type="submit" variant="primary" $fitToContent isLoading={isSubmitting}>
              <FormattedMessage {...assetViewMessages.submit} />
            </Button>
          </AssetViewFormFooter>
        )}
      </form>
    </FormProvider>
  )
}
